import { orderBy } from 'lodash';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, bind, Contract, inject, Modal, ModalHandle, TasksStore, Tooltip } from 'Mri.Web.Common';
import * as React from 'react';
import { Button, ButtonGroup, Form, FormGroup, Input } from 'reactstrap';
import { AccountStore } from '../Account/AccountStore';
import { AccountContractItem } from './AccountContractItem';
import { AccountsStore } from './AccountsStore';
import { BsFolder } from '@react-icons/all-files/bs/BsFolder';
import { BsTrash } from '@react-icons/all-files/bs/BsTrash';
import { BsArrowUpShort } from '@react-icons/all-files/bs/BsArrowUpShort';
import { ContractModal } from '../Contracts/ContractModal';
import { BsPlus } from '@react-icons/all-files/bs/BsPlus';

export interface IAccountRowProps {
    readonly account: Mri.Accounts.Account;
    readonly contracts: Contract[];
    readonly forceOpen: boolean;
    readonly history?: any;
    readonly hideIfNoExpirations?: boolean;
}

@autoinject
@observer
export class AccountItem extends React.Component<IAccountRowProps> {
    public render() {
        const location = `/accounts`;
        const { account, contracts } = this.props;
        const collapsed = this.collapsed && !this.props.forceOpen;

        return (
            <>
                {this.shouldRenderAccount &&
                    <tr className="foldering-system-row">
                        <td colSpan={7}>
                            <a onClick={this.toggleCollapse} className="btn btn-link py-0">
                                <em>{collapsed ? <BsFolder /> : <BsFolder />}</em>
                                <b>
                                    &nbsp; {account.accountName} ({contracts?.length ?? 0}) - &nbsp;
                                </b>

                                <small>
                                    <strong>Account Number: {account.accountNumber}</strong>
                                </small>
                            </a>

                            {this.hasNew && <i className="text-success px-2">{this.hasNew}</i>}
                            {this.expiring && <i className="text-warning px-2">{this.expiring}</i>}
                            {this.recentlyExpired && <i className="text-danger px-2">{this.recentlyExpired}</i>}
                        </td>
                        
                    </tr>
                }

                {this.renderModals && (
                    <>
                        <ContractModal
                            contract={{accountNumber: this.props.account.accountNumber} as Contract}
                            accountName={this.props.account.accountName}
                            viewState="create"
                            modalHandle={this.contractModalHandle}
                        />
                    </>
                )}

                {!collapsed &&
                    this.contracts?.map(c => (
                        <AccountContractItem
                            key={c.serviceCO}
                            contract={c}
                            accountName={this.props.account.accountName}
                            hideIfNoExpirations={this.props.hideIfNoExpirations}
                        />
                    ))}
                <Modal
                    className="modal-sm"
                    title="Confirm Deletion"
                    modalHandle={this.modalHandle}
                    btnClass="w-50"
                    footerClass="d-flex justify-content-center"
                >
                    <div className="text-center">{`Delete ${account.accountNumber}?`}</div>
                </Modal>
            </>
        );
    }

    @bind
    @action
    private openContractModal() {
        this.renderModals = true;

        this.contractModalHandle.open();
    }
    
    @bind
    @action
    private toggleCollapse() {
        this.collapsed = !this.collapsed;
    }

    @computed
    private get contracts() {
        return orderBy(this.props.contracts, c => c.contractEndDate() || '', 'desc');
    }

    @computed
    private get hasNew() {
        if (!this.contracts) return false;

        const filtered = this.contracts.filter(c => c.isNew());
        return filtered.length > 0 ? `${filtered.length} New!` : undefined;
    }

    @computed
    private get expiring() {
        if (!this.contracts) return;

        const filtered = this.contracts.filter(c => c.expiringSoon());

        return filtered.length > 0 ? `${filtered.length} Expiring Soon` : undefined;
    }

    @computed
    private get recentlyExpired() {
        if (!this.contracts) return;

        const filtered = this.contracts.filter(c => c.expiredRecently());

        return filtered.length > 0 ? `${filtered.length} Recently Expired` : undefined;
    }

    @computed
    private get shouldRenderAccount(){
        return !(this.props.hideIfNoExpirations && !this.expiring && !this.recentlyExpired);
    }

    @observable
    private renderModals = false;
    @observable private collapsed = true;
    private modalHandle = new ModalHandle();
    @inject
    private accountStore!: AccountStore;
    @inject
    private accountsStore!: AccountsStore;
    @inject
    private tasksStore!: TasksStore;
    private contractModalHandle = new ModalHandle();
}
